import { useEffect, useState } from "react";
import { aiCitations } from "@/lib/api";
import { Sparkles, Quote, ExternalLink } from "lucide-react";

import { log } from "@/lib/log";
const PLATFORM_COLORS = {
  Claude: "from-orange-500/20 to-orange-500/5 border-orange-400/30 text-orange-200",
  ChatGPT: "from-emerald-500/20 to-emerald-500/5 border-emerald-400/30 text-emerald-200",
  Perplexity: "from-sky-500/20 to-sky-500/5 border-sky-400/30 text-sky-200",
  Gemini: "from-blue-500/20 to-blue-500/5 border-blue-400/30 text-blue-200",
  Copilot: "from-violet-500/20 to-violet-500/5 border-violet-400/30 text-violet-200",
};

// "Cited by AI search" — public, admin-curated badge.
// Renders nothing if no active citations exist (graceful fallback).
//   Variant: `compact` → small inline pill, `full` → vertical card list.
export default function AiCitedBadge({ variant = "compact", limit = 4, className = "" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    aiCitations.list(limit).then(setData).catch((err) => {
      log.warn("AI citations failed to load:", err);
      setData({ items: [] });
    });
  }, [limit]);

  if (!data || !data.items?.length) return null;

  if (variant === "compact") {
    const platforms = Array.from(new Set(data.items.map((c) => c.platform))).slice(0, 5);
    return (
      <div
        className={`inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs ${className}`}
        data-testid="ai-cited-badge"
        title={`Cited ${data.items.length} time(s) across ${platforms.length} AI assistants`}
      >
        <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
        <span className="font-mono text-emerald-200">
          Cited by AI search
        </span>
        <span className="text-rp-textDim font-mono">·</span>
        <span className="text-rp-text">{platforms.join(" · ")}</span>
      </div>
    );
  }

  return (
    <section className={`rounded-2xl border border-rp-border bg-rp-surface p-6 ${className}`} data-testid="ai-cited-section">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-emerald-300" />
        <h2 className="font-display text-lg font-bold">Cited by AI search engines</h2>
      </div>
      <p className="mt-1 text-xs text-rp-textDim max-w-2xl">
        Real questions where Claude, ChatGPT, Perplexity and Gemini have surfaced RiskPedia as a source.
      </p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.items.map((c) => {
          const colour = PLATFORM_COLORS[c.platform] || "from-rp-surface2 to-rp-surface2 border-rp-border text-rp-text";
          return (
            <div
              key={c.citation_id}
              className={`rounded-xl border bg-gradient-to-br p-4 ${colour}`}
              data-testid={`ai-citation-${c.citation_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-mono uppercase tracking-[0.18em]">{c.platform}</span>
                {c.citation_date && (
                  <span className="text-[10px] font-mono text-rp-textFaint">{c.citation_date}</span>
                )}
              </div>
              <div className="mt-2 text-sm font-semibold text-rp-text leading-snug">
                <Quote className="inline h-3.5 w-3.5 mr-1 opacity-70" />
                {c.query}
              </div>
              {c.snippet && (
                <p className="mt-2 text-xs text-rp-textDim line-clamp-3">{c.snippet}</p>
              )}
              <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
                {c.source_url && (
                  <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="text-rp-textDim hover:text-rp-text inline-flex items-center gap-1">
                    Source page <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {c.citation_url && (
                  <a href={c.citation_url} target="_blank" rel="noopener noreferrer" className="text-rp-textDim hover:text-rp-text inline-flex items-center gap-1">
                    Screenshot / proof <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
